import BookingSummarySkeleton from '@/components/booking-summary/BookingSummarySkeleton';
import MobileBookingSummary from '@/components/booking-summary/MobileBookingSummary';
import RegularBookingSummary from '@/components/booking-summary/RegularBookingSummary';
import Consents from '@/components/Consents';
import Footer from '@/components/Footer';
import PriceDisplay from '@/components/PriceDisplay';
import AncillaryList from '@/components/purchase/checkout/ancillaries/AncillaryList';
import CancelPurchaseButton from '@/components/purchase/checkout/cancel-purchase/CancelPurchaseButton';
import BookingExpirationTimer from '@/components/purchase/checkout/expiration-timer/BookingExpirationTimer';
import PassengerList from '@/components/purchase/checkout/passengers/PassengerList';
import PaymentModal from '@/components/purchase/checkout/payment-modal/PaymentModal';
import Purchaser from '@/components/purchase/checkout/purchaser/Purchaser';
import { SeatSelection } from '@/components/purchase/checkout/seat-selection/SeatSelection';
import SectionSkeleton from '@/components/purchase/checkout/SectionSkeleton';
import Vouchers from '@/components/purchase/checkout/vouchers';
import {
  consentsSelector,
  salesPointConfigurationSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import {
  checkoutLoadingSelector,
  initiatePaymentLoadingSelector,
} from '@/features/loading/loadingSelectors';
import {
  getPurchaseFlowTravelAccountsMap,
  initiateAdyenPayment,
  initiateExternalPayment,
  initiateZeroPayment,
  updateBookingPassengers,
  updatePurchaseFlowBookingPurchaser,
} from '@/features/purchase/purchaseActions';
import {
  purchaseFlowBookingSelector,
  purchaseFlowTravelAccountsMapSelector,
} from '@/features/purchase/purchaseSelectors';
import { validateAdyenCheckoutPaymentDetails } from '@/features/purchase/purchaseService';
import useBookingSummaryData from '@/hooks/useBookingSummaryData';
import { usePaymentStatusCheck } from '@/hooks/usePaymentStatusCheck';
import type {
  PassengerFieldNames,
  PurchaserFieldNames,
} from '@/hooks/usePersonFieldRenderer';
import { usePersonFieldRenderer } from '@/hooks/usePersonFieldRenderer';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import type { TransTextKeys } from '@/i18n/trans/text';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type { BookingDTO, PayWithAdyenResponseDTO } from '@/types/dto/agent';
import type { BookingPassenger } from '@/types/passenger';
import type { PurchaseFlowBookingStorageData } from '@/types/storage';
import {
  getBookingFulfillments,
  getBookingNonTripOfferFulfillments,
  isTicketBooking,
  isTravelPassBooking,
} from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { getLocalIpAddress } from '@/utils/location';
import { purchaseFlowJourneyOrOfferPaths } from '@/utils/purchase';
import { getTenantConfiguration } from '@/utils/tenant';
import { type CheckoutValues, createCheckoutSchema } from '@/utils/zodSchema';
import { Button, Card, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const Checkout = () => {
  const dispatch = useDispatch();
  const loading = useSelector(checkoutLoadingSelector);
  const initiatePaymentLoading = useSelector(initiatePaymentLoadingSelector);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const booking = useSelector(purchaseFlowBookingSelector);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const consents = useSelector(consentsSelector);
  const { setValue: setCheckoutSuccessSessionStorage } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );
  const [paymentSession, setPaymentSession] =
    useState<PayWithAdyenResponseDTO | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectResult = searchParams.get('redirectResult');
  const [isRedirectReturn, setIsRedirectReturn] = useState(false);
  const selectedTenant = useSelector(tenantSelector);
  const tenantConfiguration = getTenantConfiguration(selectedTenant.value);
  const travelAccountsMap = useSelector(purchaseFlowTravelAccountsMapSelector);

  usePaymentStatusCheck({ booking });

  useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname === '/purchase/checkout' &&
      purchaseFlowJourneyOrOfferPaths.includes(nextLocation.pathname)
  );

  useEffect(() => {
    if (!booking || (booking && Object.keys(booking).length === 1)) {
      navigate('/purchase');
    }
  }, [booking, booking?.id, navigate]);

  useEffect(() => {
    const fulfillments = getBookingNonTripOfferFulfillments(booking);

    if (isTravelPassBooking(booking) && fulfillments.length) {
      dispatch(getPurchaseFlowTravelAccountsMap(fulfillments));
    }
  }, [booking, dispatch]);

  const {
    passengerAdmissionFields,
    passengerTravelPassFields,
    purchaserFields,
  } = usePersonFieldRenderer();

  const passengerFields = isTicketBooking(booking)
    ? passengerAdmissionFields
    : passengerTravelPassFields;

  const getPassengerDefaultValuesForConfigurableFields = useCallback(
    (p: BookingPassenger): Record<PassengerFieldNames, string> => ({
      firstName: p.firstName ?? '',
      lastName: p.lastName ?? '',
      email: p.contactInformation?.emailAddress ?? '',
      phoneNumber: p.contactInformation?.phoneNumber ?? '',
      birthDate: p.birthDate ?? '',
      gender: p.gender ?? '',
    }),
    []
  );

  const getPurchaserDefaultValuesForConfigurableFields = useCallback(
    (p: BookingDTO['purchaser']): Record<PurchaserFieldNames, string> => ({
      firstName: p?.firstName ?? '',
      lastName: p?.lastName ?? '',
      email: p?.contactInformation?.emailAddress ?? '',
      phoneNumber: p?.contactInformation?.phoneNumber ?? '',
    }),
    []
  );

  const form = useForm<CheckoutValues>({
    mode: 'onBlur',
    resolver: zodResolver(
      createCheckoutSchema(t, passengerFields, purchaserFields)
    ),
    defaultValues: {
      passengers:
        booking?.passengers?.map((p) => ({
          id: p.id,
          externalReference: p.externalReference,
          ...getPassengerDefaultValuesForConfigurableFields(p),
        })) ?? [],
      purchaser: {
        externalReference: booking?.purchaser?.externalReference,
        ...getPurchaserDefaultValuesForConfigurableFields(booking?.purchaser),
      },
      consents:
        consents?.items?.map(({ id, isMandatory, description, code }) => ({
          isMandatory,
          description,
          checked: false,
          id,
          code,
        })) ?? [],
    },
  });

  const { trigger, watch } = form;
  const currentPassengers = watch('passengers');
  const currentPurchaser = watch('purchaser');

  const sections: Array<{
    required: boolean;
    section: ReactNode;
    paragraphKey?: TransTextKeys;
    skeletonType:
      | 'passengers'
      | 'seatSelection'
      | 'ancillaries'
      | 'purchaser'
      | 'vouchers'
      | 'consents';
  }> = useMemo(
    () => [
      {
        section: <PassengerList formFields={passengerFields} />,
        required: true,
        paragraphKey: 'passengers',
        skeletonType: 'passengers',
      },
      ...(isTicketBooking(booking)
        ? [
            {
              section: <SeatSelection />,
              required: false,
              paragraphKey: 'seatSelection' as const,
              skeletonType: 'seatSelection' as const,
            },
          ]
        : []),
      ...(isTicketBooking(booking)
        ? [
            {
              section: <AncillaryList />,
              required: false,
              paragraphKey: 'extras' as const,
              skeletonType: 'ancillaries' as const,
            },
          ]
        : []),
      {
        section: (
          <Purchaser
            formFields={purchaserFields}
            passengerFields={passengerFields}
          />
        ),
        required: true,
        paragraphKey: 'payer',
        skeletonType: 'purchaser',
      },
      ...(salesPointConfiguration?.areVouchersUsed
        ? [
            {
              section: <Vouchers />,
              required: false,
              paragraphKey: 'vouchers' as const,
              skeletonType: 'vouchers' as const,
            },
          ]
        : []),
      {
        section: <Consents />,
        required: true,
        skeletonType: 'consents',
      },
    ],
    [
      booking,
      passengerFields,
      purchaserFields,
      salesPointConfiguration?.areVouchersUsed,
    ]
  );

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setPaymentSession(null);
    setIsRedirectReturn(false);
  }, []);

  const handlePaymentSuccess = useCallback(async () => {
    if (!booking?.id || !booking.code) return;

    try {
      setCheckoutSuccessSessionStorage({
        bookingId: booking.id,
        code: booking.code,
        payerEmail: currentPurchaser.email,
        fulfillments: getBookingFulfillments(booking),
      });

      navigate('/success');
    } catch (e) {
      toast.error(<TransAlert i18nKey="paymentFailed" />);
    }
  }, [
    booking,
    currentPurchaser.email,
    navigate,
    setCheckoutSuccessSessionStorage,
  ]);

  const summaryData = useBookingSummaryData({
    booking,
    passengers: currentPassengers,
    vouchers: booking?.pendingVouchers,
    displayPrice: booking?.provisionalPrice,
    travelAccountsMap,
    allowedStatuses: ['FULFILLED', 'PREBOOKED'],
  });

  useEffect(() => {
    const validatePayment = async () => {
      if (!booking?.id) return;

      const threeDsResult = searchParams.get('threeDsResult') || undefined;
      const stateData = searchParams.get('stateData') ? null : undefined;

      if (!redirectResult && !threeDsResult && !stateData) {
        return;
      }

      try {
        await validateAdyenCheckoutPaymentDetails(booking?.id, {
          ...(redirectResult && { redirectResult }),
          ...(threeDsResult && { threeDsResult }),
          ...(stateData !== undefined && { stateData }),
        });
        setIsRedirectReturn(true);
        setShowPaymentModal(true);
      } catch (error) {
        console.error('Payment validation error:', error);
        toast.error(<TransAlert i18nKey="paymentFailed" />);
      }
    };

    validatePayment();
  }, [redirectResult, searchParams, booking?.id]);

  const handleValidations = useCallback(async () => {
    const passengersValid = await trigger('passengers');
    const purchaserValid = await trigger('purchaser');
    const consentsAgreedWith = await trigger('consents');

    if (!passengersValid || !purchaserValid) {
      toast.error(<TransAlert i18nKey="mandatoryFieldsMustBeFilled" />);
      return false;
    }

    if (!consentsAgreedWith) {
      toast.error(<TransAlert i18nKey="consentsNotAgreedWith" />);
      return false;
    }

    return true;
  }, [trigger]);

  const handlePayment = useCallback(
    async (paymentCallback: () => Promise<void>) => {
      if (!booking?.id) {
        return;
      }

      const isValid = await handleValidations();

      if (!isValid) {
        return;
      }

      try {
        await dispatch(
          updateBookingPassengers({
            bookingId: booking.id,
            passengers: currentPassengers,
          })
        ).unwrap();
        await dispatch(
          updatePurchaseFlowBookingPurchaser(currentPurchaser)
        ).unwrap();

        if (booking.provisionalPrice?.amount) {
          await paymentCallback();
        } else {
          await dispatch(initiateZeroPayment(booking.id)).unwrap();
          handlePaymentSuccess();
        }
      } catch (error) {
        const reason = (error as ApiErrorResponse).response?.data.errors?.[0]
          ?.message;

        toast.error(
          reason ? (
            <div className="flex gap-1">
              <TransAlert i18nKey="paymentFailed" />
              <Typography>{reason}</Typography>
            </div>
          ) : (
            <TransAlert i18nKey="paymentFailed" />
          )
        );
      }
    },
    [
      booking?.id,
      booking?.provisionalPrice?.amount,
      currentPassengers,
      currentPurchaser,
      dispatch,
      handlePaymentSuccess,
      handleValidations,
    ]
  );

  const handleAdyenPayment = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    trackCustomEvent(GTM_EVENT.BEGIN_CHECKOUT, {
      currency: booking.provisionalPrice?.currency,
      value: booking.provisionalPrice?.amount,
    });

    const payload = {
      bookingId: booking.id,
      returnUrl: window.location.href,
      countryCode: salesPointConfiguration?.countryCode ?? 'US',
      shopperIp: await getLocalIpAddress(),
      firstName: currentPurchaser.firstName,
      lastName: currentPurchaser.lastName,
      email: currentPurchaser.email,
      phoneNumber: currentPurchaser.phoneNumber,
    };

    handlePayment(async () => {
      const data = await dispatch(initiateAdyenPayment(payload)).unwrap();

      setPaymentSession(data);
      setShowPaymentModal(true);
    });
  }, [
    booking?.id,
    booking?.provisionalPrice?.amount,
    booking?.provisionalPrice?.currency,
    currentPurchaser.email,
    currentPurchaser.firstName,
    currentPurchaser.lastName,
    currentPurchaser.phoneNumber,
    dispatch,
    handlePayment,
    salesPointConfiguration?.countryCode,
  ]);

  const handleExternalPayment = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    const payload = {
      bookingId: booking.id,
      offerPartIds: [],
      paidAmount: {
        amount: booking.provisionalPrice?.amount ?? 0,
        currency: salesPointConfiguration?.currencies?.[0],
      },
      transactionId: 'dev-transaction',
      typeId: 'SALES_POINT_PAYMENT_TYPE.EXTERNAL_3RD_PARTY',
    };

    handlePayment(async () => {
      await dispatch(initiateExternalPayment(payload)).unwrap();
      handlePaymentSuccess();
    });
  }, [
    booking?.id,
    booking?.provisionalPrice?.amount,
    dispatch,
    handlePayment,
    handlePaymentSuccess,
    salesPointConfiguration?.currencies,
  ]);

  return (
    <FormProvider {...form}>
      <div
        className="mx-auto flex w-full max-w-laptop items-start gap-8 p-4 pb-36 laptop:pb-24"
        data-testid="checkout-view"
      >
        <div className="flex w-full flex-1 flex-col gap-4">
          {/* TODO: Somehow unite this sticky section and booking summary sticky to create a border for visual separation when scrolling down*/}
          <div className="sticky top-0 z-10 flex justify-between bg-background py-2">
            <Typography variant="heading1" as="h1">
              <TransText i18nKey="checkout" />
            </Typography>
            {!isLaptopOrBigger &&
              booking &&
              Object.keys(booking).length > 1 && <BookingExpirationTimer />}
          </div>
          {sections.map(
            ({ paragraphKey, section, required, skeletonType }, key) => (
              <Card key={key} className="p-0">
                <div className="flex flex-col gap-3 p-3 pb-4 laptop:p-6 laptop:pt-4">
                  {paragraphKey && (
                    <>
                      <div className="flex gap-0.5">
                        <Typography
                          variant="subtitle"
                          className="flex gap-0.5"
                          as="h2"
                        >
                          <span>{key + 1}.</span>
                          <TransText i18nKey={paragraphKey} />
                        </Typography>
                        <Typography variant="body1" className="text-neutral">
                          {!required && (
                            <>
                              (<TransText i18nKey="optional" />)
                            </>
                          )}
                        </Typography>
                      </div>
                      <Divider variant="medium" />
                    </>
                  )}
                  <>
                    {loading && <SectionSkeleton type={skeletonType} />}
                    <div className={loading ? 'hidden' : undefined}>
                      {section}
                    </div>
                  </>
                </div>
              </Card>
            )
          )}
        </div>

        {isLaptopOrBigger && (
          <div className="sticky top-0 flex flex-col gap-4">
            <div className="flex justify-between py-2">
              <Typography variant="heading2" as="h2">
                <TransText i18nKey="bookingSummary" />
              </Typography>
              {booking && Object.keys(booking).length > 1 && (
                <BookingExpirationTimer />
              )}
            </div>
            <>
              {loading && <BookingSummarySkeleton variant="regular" />}
              <div className={loading ? 'hidden' : undefined}>
                <RegularBookingSummary {...summaryData} />
              </div>
            </>
          </div>
        )}
      </div>
      <Footer
        content={
          isLaptopOrBigger ? (
            <div className="flex items-center gap-2">
              <Icons.travelPass
                height={16}
                width={16}
                className="text-primary"
              />
              <Typography variant="subtitle">
                <TransText i18nKey="bookingTotal" />:
              </Typography>
              <Typography variant="subtitle" className="text-primary">
                <PriceDisplay amount={booking?.provisionalPrice?.amount} />
              </Typography>
            </div>
          ) : (
            <>
              {loading && <BookingSummarySkeleton variant="mobile" />}
              <div className={loading ? 'hidden' : undefined}>
                <MobileBookingSummary {...summaryData} />
              </div>
            </>
          )
        }
        actionButtons={
          <>
            <CancelPurchaseButton />
            {(tenantConfiguration?.showPayWithExternalPayment ||
              isLocalDevelopment()) && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="confirm-purchase-button-external"
                onClick={handleExternalPayment}
                variant="cta"
                className="rounded-lg laptop:w-auto"
                isPending={initiatePaymentLoading}
                startAdornment={<Icons.lock />}
              >
                <TransText i18nKey="externalPay" />
              </Button>
            )}
            {(tenantConfiguration?.showPayWithAdyen ||
              isLocalDevelopment()) && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="confirm-purchase-button"
                className="rounded-lg laptop:w-auto"
                onClick={handleAdyenPayment}
                aria-label="Pay button"
                variant="cta"
                isPending={initiatePaymentLoading}
                startAdornment={<Icons.lock />}
              >
                <TransText i18nKey="pay" />
                {isLaptopOrBigger && (
                  <PriceDisplay amount={booking?.provisionalPrice?.amount} />
                )}
              </Button>
            )}
          </>
        }
      />
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        onPaymentSuccess={handlePaymentSuccess}
        paymentSession={paymentSession}
        bookingId={booking?.id}
        bookingAmount={booking?.provisionalPrice?.amount}
        bookingCurrency={booking?.provisionalPrice?.currency}
        isRedirectReturn={isRedirectReturn}
      />
    </FormProvider>
  );
};

export default Checkout;
